import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useHandleInvitations } from '../hooks/useHandleInvitation';

import { ModalDesafioMasInformacion } from './ModalDesafioMasInformacion';
import { ChallengeTotalStats } from './totalStats';

import { Button } from '@/components';
import {
  Sidebar,
  TourneyList,
  TourneySearch,
} from '@/components/GenericsTournaments';
import { NotFoundResult } from '@/components/NotFoundResult';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import { searchUser } from '@/services/challenges';
import { Maybe } from '@/types';
import {
  ChallengeInitResponse,
  ChallengeStatus,
  UserChallengeSearchTO,
} from '@/types/challenges';
import { GenericItemTourneysData } from '@/types/tda';
import { TEXTS } from '@/utils';

const winLoseText = ({ wins, losses }: { wins?: number; losses?: number }) => {
  if (wins === undefined && losses === undefined) return '';
  if (wins === undefined) return `PERDIÓ ${losses}`;
  if (losses === undefined) return `GANÓ ${wins}`;
  return `GANÓ ${wins} - PERDIÓ ${losses}`;
};

interface ParamsRequest {
  valueSearch: string;
  page: number;
}

export const SideBarContent = ({
  list,
  init,
}: {
  list: UserChallengeSearchTO[];
  init: Maybe<ChallengeInitResponse>;
}) => {
  const toast = useToast();
  const { isDesktop } = useIsDesktop();

  const [searchResult, setSearchResult] = useState<
    GenericItemTourneysData[] | null
  >(null);

  const [recomendedUsers, setRecomendedUsers] = useState<
    GenericItemTourneysData[]
  >([]);
  const [sendForm, setSendForm] = useState(false);

  const { ModalInfo, setIsModalDesafioOpen } = ModalDesafioMasInformacion();
  const { userInitialData } = useAuth();
  const { tourneyData } = useTourney();

  const { onHandleSubmit } = useHandleInvitations(recomendedUsers);

  const { onHandleSubmit: onHandleSubmitPostulation } =
    useHandleInvitations(searchResult);

  const GDTisOver =
    userInitialData?.veda &&
    userInitialData?.fechaActiva.id ===
      userInitialData?.todasLasFechas[
        userInitialData?.todasLasFechas.length - 1
      ]?.id;

  const disabledActions = useCallback(
    (user: UserChallengeSearchTO) => {
      return init?.desafios.some(
        (desafio) =>
          desafio.idEstadoDesafio !==
            ChallengeStatus.PENDIENTE_PUEDE_CONFIRMAR &&
          (desafio.idUsuarioRival === user.idUsuario ||
            desafio.idUsuarioDesafiador === user.idUsuario)
      );
    },
    [init?.desafios]
  );

  const initState = useCallback(() => {
    if (list && list.length > 0) {
      const recommend = list
        .slice(0, 4)
        .filter((user) => user.idUsuario !== userInitialData?.idUsuarioDT)
        .map((user) => ({
          id: user.idUsuario,
          title: `${user.nombres} ${user.apellido} `,
          subtitle: user.nombreEquipo,
          secundaryText: winLoseText({
            wins: user.ganados,
            losses: user.perdidos,
          }),
          disabled: disabledActions(user) || GDTisOver,
          action: {
            txtAction: 'Desafiar',
            txtActionDisabled:
              GDTisOver && !disabledActions(user) ? 'Desafiar' : 'Desafiado',
            variantButton: 'secondary' as const,
          },
        }));

      setRecomendedUsers(() => {
        return recommend;
      });
    } else {
      setRecomendedUsers(() => {
        return [];
      });
    }
  }, [GDTisOver, disabledActions, list, userInitialData?.idUsuarioDT]);

  useEffect(() => {
    initState();

    return () => {
      setRecomendedUsers(() => {
        return [];
      });
    };
  }, [initState]);

  // Handle search request
  const onHandleRequest = useCallback(
    async ({ valueSearch }: ParamsRequest) => {
      const trimmedSearch = valueSearch.trim();

      // Validate minimum 3 characters
      if (trimmedSearch.length < 3) {
        setSearchResult(trimmedSearch.length === 0 ? null : []);
        return;
      }

      setSendForm(true);

      try {
        const response = await searchUser(trimmedSearch);

        const searchResults =
          response?.usuarios
            ?.filter((user) => user.idUsuario !== userInitialData?.idUsuarioDT)
            .map((user) => ({
              id: user.idUsuario,
              title: `${user.nombres} ${user.apellido}`,
              subtitle: user.nombreEquipo,
              secundaryText: winLoseText({
                wins: user.ganados,
                losses: user.perdidos,
              }),
              // image: user.fotoPerfil,
              disabled: disabledActions(user),
              action: {
                txtAction: 'Desafiar',
                txtActionDisabled: 'Desafiado',
                variantButton: 'secondary' as const,
              },
            })) || [];

        setSearchResult(searchResults);
      } catch (error) {
        setSearchResult([]);
        console.error(error);
        toast.show('Error al buscar usuarios');
      }
    },
    [disabledActions, toast, userInitialData?.idUsuarioDT]
  );

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;

      // Clear previous timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (value.length > 2) {
        debounceRef.current = setTimeout(() => {
          onHandleRequest({ valueSearch: value, page: 1 });
        }, 300);
      } else {
        setSearchResult(value.length === 0 ? null : []);
      }
    },
    [onHandleRequest]
  );

  const onHandleRefreshAfterInvitation = useCallback(
    (ids: number[], isPendings = true) => {
      const numberSet = new Set(ids);

      const disabledParticipant = {
        action: {
          txtAction: 'Desafiar',
          txtActionDisabled: 'Desafiado',
        },
      };

      setSearchResult((prevSearchResult) => {
        if (!prevSearchResult) return null;
        return prevSearchResult.map((item) => ({
          ...item,
          disabled: (item.id && numberSet.has(item.id)) || item.disabled,
          ...(!isPendings && disabledParticipant),
        }));
      });

      setRecomendedUsers((prevRecommend) => {
        if (!prevRecommend) return [];
        return prevRecommend.map((item) => ({
          ...item,
          disabled: (item.id && numberSet.has(item.id)) || item.disabled,
          ...(!isPendings && disabledParticipant),
        }));
      });
    },
    []
  );

  useEffect(() => {
    if (tourneyData.idsPendings.length > 0) {
      onHandleRefreshAfterInvitation(tourneyData.idsPendings, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPendings]);

  useEffect(() => {
    if (tourneyData.idsPlayings.length > 0) {
      onHandleRefreshAfterInvitation(tourneyData.idsPlayings, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPlayings]);

  return (
    <Sidebar className="item-center flex flex-col justify-center">
      {isDesktop && init && init.hist_jugados && (
        <ChallengeTotalStats init={init} />
      )}
      {/* Search for users */}
      <TourneySearch
        autocomplete={false}
        className="mt-2 lg:mt-11 px-4 md:px-0"
        disabled={GDTisOver}
        label="Desafiá a un DT"
        placeholder="Buscá un DT para desafiar"
        sendForm={sendForm}
        tabSelected={TabType.FirstTab}
        onHandleCleanResult={() => {
          setSearchResult(null);
        }}
        onHandleKeyUp={handleKeyUp}
        onHandleRequest={onHandleRequest}
        onHandleTabSelected={() => null}
      />

      {/* Search results */}
      {searchResult && searchResult.length > 0 && (
        <>
          <TourneyList
            paginator
            action={{
              txtAction: 'Desafiar',
              txtActionDisabled: 'Desafiado',
              variantButton: 'secondary',
            }}
            className="mt-4"
            classNames={{
              secondaryText: 'text-blue-500 text-3xs',
              titleContainer: 'px-4 md:px-0',
            }}
            classPaginator="paginator-sidebar"
            disabled={true}
            handleSubmit={onHandleSubmitPostulation}
            hasMore={true}
            list={searchResult}
            title="Resultados de búsqueda"
            variant="default"
          />

          {/* {isDesktop && (
            <div className="bg-gdt-gray-7 rounded-b-lg max-w-full">
              {allSearchResults.length > 4 && (
                <Paginator
                  className="!my-0"
                  currentContentPage={handlePageChange}
                  items={allSearchResults}
                  itemsPerPage={4}
                  marginPagesDisplayed={1}
                  pageRangeDisplayed={3}
                />
              )}
            </div>
          )} */}
        </>
      )}

      {/* No search results found */}
      {searchResult && searchResult.length === 0 && (
        <NotFoundResult title={TEXTS.notFoundResult} />
      )}

      {/* Recommended rivals */}
      {recomendedUsers.length > 0 && (
        <TourneyList
          className="mt-4"
          classNames={{
            secondaryText: 'text-blue-500 text-3xs',
            titleContainer: 'px-4 md:px-0',
          }}
          handleSubmit={onHandleSubmit}
          list={recomendedUsers}
          title="Rivales sugeridos"
          variant="default"
        />
      )}

      {((init && init?.desafios?.length > 0) || !isDesktop) && (
        <Button
          className="font-bold mt-4 cursor-pointer mx-auto"
          type="button"
          variant="text"
          onClick={() => setIsModalDesafioOpen(true)}
        >
          Más Información
        </Button>
      )}

      <ModalInfo />
    </Sidebar>
  );
};

export default React.memo(SideBarContent);

SideBarContent.displayName = 'SideBarContent';
